import { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BasketProvider } from '@/context/BasketContext';
import { ThemeProvider } from '@/context/ThemeContext';
import ErrorBoundary from '@/components/organisms/ErrorBoundary';
import Spinner from '@/components/atoms/Spinner';
import MainLayout from '@/layouts/MainLayout';
import { useEmbedMessaging } from '@/hooks/useEmbedMessaging';

const HomePage = lazy(() => import('@/pages/HomePage'));
const CatalogPage = lazy(() => import('@/pages/CatalogPage'));
const ProductPage = lazy(() => import('@/pages/ProductPage'));
const CategoryPage = lazy(() => import('@/pages/CategoryPage'));
const BasketPage = lazy(() => import('@/pages/BasketPage'));
const EnquiriesPage = lazy(() => import('@/pages/EnquiriesPage'));
const ContactPage = lazy(() => import('@/pages/ContactPage'));
const ServicesPage = lazy(() => import('@/pages/ServicesPage'));
const PrivacyPolicyPage = lazy(() => import('@/pages/PrivacyPolicyPage'));
const TermsAndConditionsPage = lazy(() => import('@/pages/TermsAndConditionsPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Inner component — needs to be inside <Router> to use useLocation via useEmbedMessaging
function AppRoutes() {
  useEmbedMessaging();

  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><Spinner size="lg" /></div>}>
      <Routes>
        <Route element={<MainLayout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/shop" element={<CatalogPage />} />
          <Route path="/products/:id" element={<ProductPage />} />
          <Route path="/category/:slug" element={<CategoryPage />} />
          <Route path="/basket" element={<BasketPage />} />
          <Route path="/checkout" element={<BasketPage />} />
          <Route path="/enquiries" element={<EnquiriesPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BasketProvider>
        <ThemeProvider>
          <ErrorBoundary>
            <AppRoutes />
          </ErrorBoundary>
        </ThemeProvider>
      </BasketProvider>
    </QueryClientProvider>
  );
}
